import {
  Card,
  Divider,
  ModalDialog,
  PrimaryButton,
  SecondaryButton,
  Space,
  TertiaryButton,
} from '@procurify/ui'

import { Tabs } from '@webapp/core/components/Tabs'
import { useAppRoutes, useProcurifyIntl } from '@webapp/hooks'
import { CreateCreditCardItemFormPresenter } from '@webapp/modules/accounts-payable/features/create-credit-card-item/presenters/CreateCreditCardItemFormPresenter'
import { EditCreditCardItemFormPresenter } from '@webapp/modules/accounts-payable/features/edit-credit-card-item/presenters/EditCreditCardItemFormPresenter'
import { CreditCardStatementDetailsPresenter } from '@webapp/modules/accounts-payable/features/get-credit-card-account/presenters/CreditCardStatementDetailsPresenter'
import { CreditCardItemListPresenter } from '@webapp/modules/accounts-payable/features/list-credit-card-items/presenters/CreditCardItemListPresenter'
import {
  MoveItemsActionBar,
  MoveItemsWarningContent,
} from '@webapp/modules/accounts-payable/features/move-credit-card-items/presenters/MoveCreditCardItemsPresenter'
import { StartReconciliationSessionFormPresenter } from '@webapp/modules/accounts-payable/features/start-reconciliation-session/presenters/StartReconciliationSessionFormPresenter'
import {
  AccountDetailsPageTabs,
  type AccountDetailsPageTabEnum,
} from '@webapp/modules/accounts-payable/pages/types'
import messages from './messages'
import { useCreditCardAccountDetailsView } from './useCreditCardAccountDetailsView'

export interface ICreditCardAccountDetailsViewProps {
  accountId: number
  tab: AccountDetailsPageTabEnum
}

/**
 * Feature Set: Credit Card Account Details View (Page)
 *
 * Orchestrates the statement details panel, the Active/Archived item tabs
 * (URL-synced), the row-menu composition of four command features, the
 * move-mode selection bridge + sticky action bar, the lock guard (BHV-007),
 * and all FS-owned modals (item create/edit/view, reconcile, move warning).
 */
export const CreditCardAccountDetailsView = ({
  accountId,
  tab,
}: ICreditCardAccountDetailsViewProps) => {
  const intl = useProcurifyIntl()
  const { url } = useAppRoutes()

  const {
    state: {
      account,
      archived,
      itemModal,
      isReconcileModalOpen,
      moveMode,
      selectedItems,
      moveWarning,
      isMoving,
      targetCard,
      selectionTotal,
    },
    methods: {
      openCreateItemModal,
      closeItemModal,
      openReconcileModal,
      closeReconcileModal,
      enterMoveMode,
      exitMoveMode,
      clearSelection,
      handleSelectionChange,
      renderItemRowMenu,
      handleMove,
      handleMoveConfirm,
      dismissMoveWarning,
      handleReconcileSuccess,
      onTargetCardChange,
    },
  } = useCreditCardAccountDetailsView({ accountId, tab })

  return (
    <>
      <Space>
        <Card>
          <Card.Content>
            <CreditCardStatementDetailsPresenter
              accountId={accountId}
              onReconcileClick={openReconcileModal}
            />
          </Card.Content>
        </Card>

        <div>
          <Tabs>
            <Tabs.TabLink
              to={url('CreditCardAccountDetailsPage', {
                id: String(accountId),
                tab: AccountDetailsPageTabs.ACTIVE,
              })}
            >
              {intl.formatMessage(messages.activeTabLabel)}
            </Tabs.TabLink>
            <Tabs.TabLink
              to={url('CreditCardAccountDetailsPage', {
                id: String(accountId),
                tab: AccountDetailsPageTabs.ARCHIVED,
              })}
            >
              {intl.formatMessage(messages.archivedTabLabel)}
            </Tabs.TabLink>
          </Tabs>
          <Card>
            <Card.Content>
              <Space size='large'>
                {/* Add/Move hidden on the Archived tab (BHV-012); swapped for
                    Cancel while move mode is on (BHV-021). */}
                {!archived && (
                  <Space direction='horizontal' justify='end' align='center'>
                    {moveMode ? (
                      <TertiaryButton type='button' onClick={exitMoveMode}>
                        {intl.formatMessage(messages.cancelMoveModeButton)}
                      </TertiaryButton>
                    ) : (
                      <Space direction='horizontal' align='center'>
                        <SecondaryButton type='button' onClick={enterMoveMode}>
                          {intl.formatMessage(messages.moveItemsButton)}
                        </SecondaryButton>
                        <SecondaryButton
                          type='button'
                          icon='add'
                          onClick={openCreateItemModal}
                        >
                          {intl.formatMessage(messages.addItemButton)}
                        </SecondaryButton>
                      </Space>
                    )}
                  </Space>
                )}

                <CreditCardItemListPresenter
                  accountId={accountId}
                  archived={archived}
                  selectionMode={moveMode && !archived}
                  selectedItemIds={selectedItems.map((item) => item.id)}
                  onSelectionChange={handleSelectionChange}
                  renderRowMenu={renderItemRowMenu}
                />
              </Space>
            </Card.Content>
          </Card>
        </div>
      </Space>

      {moveMode && !archived && account && (
        <MoveItemsActionBar
          selectedCount={selectedItems.length}
          selectedTotalLabel={selectionTotal}
          sourceAccountId={account.id}
          targetCard={targetCard}
          onTargetCardChange={onTargetCardChange}
          onDeselectAll={clearSelection}
          onMove={handleMove}
          isMoving={isMoving}
        />
      )}

      <ModalDialog
        title={intl.formatMessage(messages.createItemModalTitle)}
        maxWidth='lg'
        open={itemModal?.mode === 'create'}
        onClose={closeItemModal}
      >
        {itemModal?.mode === 'create' && account && (
          <CreateCreditCardItemFormPresenter
            account={account}
            onSuccess={closeItemModal}
            onCancel={closeItemModal}
          />
        )}
      </ModalDialog>

      <ModalDialog
        title={intl.formatMessage(messages.editItemModalTitle)}
        maxWidth='lg'
        open={itemModal?.mode === 'edit' || itemModal?.mode === 'view'}
        onClose={closeItemModal}
      >
        {(itemModal?.mode === 'edit' || itemModal?.mode === 'view') &&
          itemModal.item && (
            <EditCreditCardItemFormPresenter
              item={itemModal.item}
              mode={itemModal.mode}
              onSuccess={closeItemModal}
              onCancel={closeItemModal}
            />
          )}
      </ModalDialog>

      <ModalDialog
        title={intl.formatMessage(messages.reconcileModalTitle)}
        maxWidth='md'
        open={isReconcileModalOpen}
        onClose={closeReconcileModal}
      >
        {isReconcileModalOpen && account && (
          <StartReconciliationSessionFormPresenter
            account={account}
            onSuccess={handleReconcileSuccess}
            onCancel={closeReconcileModal}
          />
        )}
      </ModalDialog>

      {/* FS-owned warning modal — the feature holds the content only
          (BHV-023). */}
      <ModalDialog
        title={intl.formatMessage(messages.moveWarningModalTitle)}
        maxWidth='sm'
        open={moveWarning !== null}
        onClose={dismissMoveWarning}
      >
        {moveWarning && (
          <Space>
            <MoveItemsWarningContent
              affectedItems={moveWarning.affectedItems}
              totalCount={moveWarning.allItemIds.length}
            />
            <Divider />
            <Space direction='horizontal' justify='end' align='center'>
              <TertiaryButton type='button' onClick={dismissMoveWarning}>
                {intl.formatMessage(messages.moveWarningDismissButton)}
              </TertiaryButton>
              <PrimaryButton type='button' onClick={handleMoveConfirm}>
                {intl.formatMessage(messages.moveWarningConfirmButton)}
              </PrimaryButton>
            </Space>
          </Space>
        )}
      </ModalDialog>
    </>
  )
}

CreditCardAccountDetailsView.displayName = 'CreditCardAccountDetailsView'
